import { useContext, useMemo, useRef } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

import { signal } from "@preact/signals-react";
import { createAppState } from "./AppState";

const projectQuery = signal("");

export default function SelectProject() {
    const { projects, selectedProject } = useContext(createAppState);

    const searchInputRef = useRef(null);

    const filteredProjects = useMemo(() => {
        const q = projectQuery.value.trim().toLowerCase();
        if (!q) return projects.value;
        return projects.value.filter((p) =>
            (p?.name ?? "").toLowerCase().includes(q)
        );
    }, [projects.value, projectQuery.value]);

    const disabled = projects.value.length <= 0;

    const MenuProps = {
        PaperProps: { style: { maxHeight: 800, width: 'inherit' } },
        disableAutoFocusItem: true,
        MenuListProps: {
            autoFocusItem: false,
            // if focus ever leaves the input, snap it back so typing never drives selection
            onKeyDown: (e) => {
                if (e.key === "Escape") return; // allow closing
                // Keep focus in the search field while menu is open
                if (document.activeElement !== searchInputRef.current) {
                    searchInputRef.current?.focus?.();
                }
                // Stop the Select/MenuList from using keystrokes for "type-to-select"
                e.stopPropagation();
            },
            onKeyUp: (e) => e.stopPropagation(),
            onKeyPress: (e) => e.stopPropagation(),
        },

        TransitionProps: {
            onEntered: () => {
                // Focus search as soon as the menu opens
                searchInputRef.current?.focus?.();
            },
        },
    };

    return (
        <FormControl fullWidth variant="filled" disabled={disabled}>
            <InputLabel id="project-label" required>
                Project
            </InputLabel>

            <Select
                labelId="project-label"
                id="select-projects"
                value={selectedProject.value}
                label="Project *"
                onChange={(event) => {
                    selectedProject.value = event.target.value;
                }}
                onClose={() => {
                    // optional: clear search on close
                    projectQuery.value = "";
                }}
                MenuProps={MenuProps}
            >
                <ListSubheader
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        bgcolor: "background.paper",
                        py: 1,
                    }}
                >
                    <Box sx={{ px: 1 }}>
                        <TextField
                            inputRef={searchInputRef}
                            value={projectQuery.value}
                            onChange={(e) => (projectQuery.value = e.target.value)}
                            placeholder="Search projects…"
                            variant="outlined"
                            size="small"
                            fullWidth
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (e.key === "Escape") return; // let ESC close
                                e.stopPropagation();
                            }}
                            onKeyUp={(e) => e.stopPropagation()}
                            onKeyPress={(e) => e.stopPropagation()}
                        />
                    </Box>
                </ListSubheader>

                {filteredProjects.map((project) => (
                    <MenuItem value={project.id} key={project.id}>
                        {project.name}
                    </MenuItem>
                ))}

                {!disabled && filteredProjects.length === 0 && (
                    <MenuItem disabled value="">
                        No matches
                    </MenuItem>
                )}
            </Select>
        </FormControl>
    );
}
